/**
 * Client for the customer onboarding leads.
 *
 * The contract between tri-server and this file is tri-server `docs/onboarding-leads.md`; paths,
 * field names, statuses and error codes are not changed here on our own.
 *
 * Two halves with different rules:
 *
 * - **Public** (`/public/onboarding/:token`): called by a prospect who has no account. Plain
 *   `fetch`/`XMLHttpRequest`, never `authenticatedFetch` - that one would add a bearer token of
 *   whoever happens to be signed in in this browser and send a 401 to `/login`. The token in the
 *   path is the credential.
 * - **Admin** (`/administration/onboarding-leads`): `authenticatedFetch` like every other
 *   administration call.
 */
import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

// ---------------------------------------------------------------------------
// Types (contract)
// ---------------------------------------------------------------------------

export type AssetClass = "residential" | "office" | "retail" | "industrial" | "datacenter" | "hotel" | "other";
export const ASSET_CLASSES: AssetClass[] = ["residential", "office", "retail", "industrial", "datacenter", "hotel", "other"];

export type DataSlot =
  | "buildings"
  | "assets"
  | "spaces"
  | "floorplans"
  | "assetDetails"
  | "maintenance"
  | "responsibilities"
  | "catalogs"
  | "escapePlans"
  | "other"
  | "logo";

export type LeadStatus = "invited" | "in_progress" | "submitted" | "analyzing" | "report_ready";
export type AnalysisState = "idle" | "running" | "done" | "failed";
export type CustomerStatus = "open" | "received" | "analyzing" | "report_sent";
export type YesNoUnknown = "yes" | "no" | "unknown";
export type Ownership = "owner" | "operator" | "both";

export interface LeadProfile {
  companyName: string;
  legalName?: string;
  street?: string;
  streetNumber?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  website?: string;
  /** First and last name apart (the account is made for this name); both are required to submit. */
  contactFirstName?: string;
  contactLastName?: string;
  /** Made by the server from the two names above: shown, never sent. */
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  assetClass?: AssetClass;
  ownership?: Ownership;
  logoFileId?: string;
}

export interface LeadAnswers {
  stableIds?: YesNoUnknown;
  labelsInstalled?: YesNoUnknown;
  labelNumberNote?: string;
  criticalAreas?: string;
  floorplanVersionNote?: string;
  sourceSystem?: string;
  /** About the escape and rescue plans (the server added these with the `escapePlans` slot). */
  escapePlansStatus?: EscapePlansStatus;
  escapePlansNote?: string;
}

export type EscapePlansStatus = "current" | "outdated" | "unknown" | "none";
export const ESCAPE_STATUSES: EscapePlansStatus[] = ["current", "outdated", "unknown", "none"];

export interface PublicFileView {
  id: string;
  slot: DataSlot;
  originalName: string;
  size: number;
  uploadedAt: string;
}

export interface PublicSlotInfo {
  slot: DataSlot;
  number?: number;
  required: boolean;
  extensions: string[];
  maxFiles?: number;
}

/** A personal note from the sender, shown on the welcome page. Optional: nothing is shown without it. */
export interface PublicWelcome {
  senderName?: string;
  message?: string;
}

/**
 * The state of the mobile number check (a code by SMS before the submit). `number` is masked by the server
 * ("+49 171 ••• 4567"); `nextSendAt` is when a new code may be sent (absent: at once). An older server has none of it.
 */
export interface PublicPhoneState {
  number?: string;
  verified: boolean;
  codeSent: boolean;
  nextSendAt?: string;
}

export interface PublicLeadView {
  customerStatus: CustomerStatus;
  /** The sender's personal welcome, when the invitation carries one. */
  welcome?: PublicWelcome;
  expiresAt: string;
  profile: LeadProfile;
  answers: LeadAnswers;
  files: PublicFileView[];
  slots: PublicSlotInfo[];
  limits: { maxFileSizeBytes: number; maxFiles: number; /** the logo has its own, smaller limit (server contract, added later) */ maxLogoBytes?: number };
  canEdit: boolean;
  /** Present once the server checks the mobile number; then the number is required and a step confirms it. */
  phone?: PublicPhoneState;
  /** `enabled`: the server can look an address up from the company name (see `companyLookup`). */
  lookup?: { enabled?: boolean };
}

/** What `POST .../company-lookup` answers: found (with a suggestion and where it comes from), nothing, or not available. */
export interface CompanyLookupResult {
  status: "found" | "not_found" | "unavailable";
  suggestion?: { legalName?: string; street?: string; streetNumber?: string; postalCode?: string; city?: string; country?: string; website?: string };
  source?: { host?: string; url?: string };
}

/**
 * What the server answers once the lead has left "open": no profile, answers, files, slots or limits,
 * only the status, the expiry, the first name for the greeting and, if any, the welcome. The client
 * accepts both forms and builds nothing on the absence of the slim one's fields.
 */
export interface SlimLeadView {
  customerStatus: Exclude<CustomerStatus, "open">;
  expiresAt?: string;
  canEdit: false;
  firstName?: string;
  welcome?: PublicWelcome;
  /** From the submit on, in every status: the e-mail with the access data went out. Absent or false: the access is still being made. */
  accessSent?: boolean;
  /** The first administrator's access exists, so a one-time sign-in can be made (see `portalLogin`). */
  accountReady?: boolean;
}

export type PublicLeadResponse = PublicLeadView | SlimLeadView;

/** The slim form: no `slots` list. A full form of a lead that is no longer open is treated as slim, too. */
export const isSlimView = (view: PublicLeadResponse): view is SlimLeadView =>
  !Array.isArray((view as PublicLeadView).slots) || view.customerStatus !== "open";

/** Error codes of the public routes (contract: "Fehlerformat"). */
export type PublicErrorCode =
  | "invalid_token"
  | "token_expired"
  | "locked"
  | "invalid_slot"
  | "invalid_file_type"
  | "file_too_large"
  | "too_many_files"
  | "missing_required"
  | "rate_limited"
  // added by the server implementation (contract, "Fehlerformat")
  | "invalid_request"
  | "missing_file"
  | "not_found"
  // the mobile number check
  | "invalid_code"
  | "code_expired"
  | "too_many_attempts"
  | "sms_failed"
  | "phone_not_verified"
  // the direct sign-in after the submit
  | "not_ready"
  | "invalid_handle"
  | "expired"
  | "already_used"
  | "unavailable";

/**
 * What became of the tenant for this lead (the server creates it when the report is released): `pending` not
 * yet, `done` tenant and first admin user created, `existing_user` the e-mail address already had an account (nothing
 * was created), `failed` creating went wrong. No password appears in any answer of the server.
 */
export type ProvisioningState = "pending" | "done" | "existing_user" | "failed";

export interface LeadProvisioningSummary {
  state: ProvisioningState;
  customerId?: string;
  /** The contact signs in with SSO: no password was set, and none can be sent. */
  ssoOnly?: boolean;
}

export interface LeadProvisioningDetail extends LeadProvisioningSummary {
  credentialsMailSentAt?: string;
  completedAt?: string;
  error?: string;
  customerName?: string;
}

/** The uploads moving into the tenant's data space (and the logo becoming the tenant's logo) after the tenant exists. */
export type AdoptionState = "waiting_for_customer" | "pending" | "running" | "done" | "failed";

export interface LeadAdoption {
  state: AdoptionState;
  movedFiles: number;
  totalFiles: number;
  logoAdopted?: boolean;
  /** File names that moved into the tenant but were not evaluated (the processing budget per lead was used up). Newer servers only. */
  notProcessed?: string[];
  /** File names whose copy was checked by size only, not by hash. Newer servers only. */
  sizeOnlyChecked?: string[];
  error?: string;
  startedAt?: string;
  completedAt?: string;
}

/** Site, buildings and assets made from the adopted files. */
/** `not_started`: the files are in the tenant and an administrator looks at them first and starts the import by hand. */
export type ImportState = "not_started" | "waiting_for_files" | "waiting_for_buildings" | "running" | "done" | "failed" | "skipped";

export interface LeadImport {
  state: ImportState;
  siteId?: string;
  siteName?: string;
  buildings?: { created: number; skipped: number };
  assets?: { jobId?: string; created?: number; updated?: number; unassigned?: number; summary?: string };
  /** Not shown. */
  processingRetries?: number;
  /** Notes on the import, for example that cells were cut to 500 characters. Newer servers only. */
  warnings?: string[];
  error?: string;
  startedAt?: string;
  completedAt?: string;
}

export interface AdminLeadSummary {
  id: string;
  status: LeadStatus;
  analysisState: AnalysisState;
  companyName: string;
  /** Empty or absent until the customer has entered the name in the profile. */
  contactName?: string;
  contactEmail: string;
  assetClass?: AssetClass;
  createdAt: string;
  submittedAt?: string;
  fileCount: number;
  tokenExpiresAt: string;
  /** How often the page was opened in a browser (a tab session counts once), and when; added by the server later: absent from an older one. */
  openCount?: number;
  firstOpenedAt?: string;
  lastOpenedAt?: string;
  lastActiveAt?: string;
  /** The furthest step reached, as the server's slug: welcome, company, buildings, assets, spaces, floorplans, more, questions, review. */
  furthestStep?: string;
  /** Whether the mobile number was confirmed by a code; newer servers only. */
  phoneVerified?: boolean;
  /** Added by the server later: absent from an older one. */
  provisioning?: LeadProvisioningSummary;
  /** Only the states; added by the server later: absent from an older one. */
  adoptionState?: AdoptionState;
  importState?: ImportState;
  /** The contact's e-mail address already has an account (or another open invitation): newer servers only. */
  contactConflict?: boolean;
}

/** A notice the server hands back with a created invitation; the invitation exists anyway. */
export interface InviteWarning {
  code: "existing_user" | "open_lead";
  message: string;
}

export type CanonicalField =
  | "id"
  | "name"
  | "building"
  | "floor"
  | "space"
  | "area"
  | "usageClass"
  | "address"
  | "yearBuilt"
  | "manufacturer"
  | "model"
  | "serialNumber"
  | "installed"
  | "lastMaintained"
  | "nextMaintenance"
  | "interval"
  | "legalBasis"
  | "responsibleRole"
  | "condition"
  | "category";

export interface AnalysisReportFile {
  fileId: string;
  slot: DataSlot;
  originalName: string;
  size: number;
  kind: "table" | "plan" | "document" | "image" | "archive";
  table?: {
    delimiter?: string;
    encoding?: string;
    rowCount: number;
    columns: { header: string; canonical?: CanonicalField; filledShare: number }[];
  };
  note?: string;
}

export interface AnalysisReport {
  generatedAt: string;
  files: AnalysisReportFile[];
  checklist: {
    slot: DataSlot;
    number: number;
    required: boolean;
    state: "present" | "partial" | "missing";
    findings: string[];
  }[];
  assetQuality?: { rows: number; duplicateIds: number; /** ids that occur in more than one file (newer servers) */ idsInMultipleFiles?: number; emptyIds: number; genericNameShare: number };
  answersSummary: string[];
}

export interface AdminLeadDetail extends AdminLeadSummary {
  provisioning?: LeadProvisioningDetail;
  /** The mobile number check of this lead: the full number, and how far it got. Newer servers only. */
  phoneVerification?: { state: "none" | "pending" | "verified"; phone?: string; verifiedAt?: string };
  adoption?: LeadAdoption;
  import?: LeadImport;
  invitation: {
    companyName: string;
    contactName?: string;
    contactEmail: string;
    assetClass?: AssetClass;
    note?: string;
    /** Public, unlike the note: shown on the customer's welcome page. */
    senderName?: string;
    welcomeMessage?: string;
  };
  /** The last (up to 20) times the page was opened. */
  recentOpens?: string[];
  profile: LeadProfile;
  answers: LeadAnswers;
  /** `storage` says where the bytes are now: still with the onboarding, or moved into the tenant's data space. */
  files: (PublicFileView & { contentType: string; storage?: "onboarding" | "tenant" })[];
  /** Mail state and recipient, added by the server implementation. */
  notifications?: { submittedMailSentAt?: string; reportMailSentAt?: string };
  customerNotifiedAt?: string;
  customerMailTo?: string;
  analysis: {
    state: AnalysisState;
    startedAt?: string;
    finishedAt?: string;
    error?: string;
    report?: AnalysisReport;
  };
}

/** What an invitation needs: the company, the contact's address and how long the link lives. Nothing else is sent. */
export interface CreateLeadInput {
  companyName: string;
  contactEmail: string;
  expiresInDays?: number;
}

// ---------------------------------------------------------------------------
// Public half
// ---------------------------------------------------------------------------

/**
 * What the server shows of a stored file before the submit (`GET .../files/:id/preview`): a table (at most 50 rows and
 * 30 columns), a file the browser can show by itself (an image or a PDF, fetched from `.../content`), or nothing.
 */
export type FilePreview =
  | { kind: "table"; headers: string[]; rows: unknown[][]; totalRows: number; truncated: boolean; sheetName?: string }
  | { kind: "inline"; contentType: string; size: number; url?: string }
  | { kind: "none"; reason: "unsupported" | "unreadable" };

/** A refused or failed public call; `code` is the contract's error code, or `network` / `unknown`. */
export class OnboardingApiError extends Error {
  status: number;
  code: PublicErrorCode | "network" | "unknown";
  /** `missing_required` names what is missing: `files`, `profile.companyName`, ... */
  missing?: string[];
  /** Seconds from the `Retry-After` header of a 429. */
  retryAfterSeconds?: number;
  /** `invalid_request` names the technical field it is about (`streetNumber`, `website`, ...); the page shows its own text at that field. */
  field?: string;
  /** Why a file was refused (`invalid_file_type`): `extension` (the name) or `content` (the bytes do not match the name). */
  reason?: string;
  /** `invalid_code`: how many tries are left. */
  attemptsLeft?: number;
  /** `rate_limited` of a code request: from when a new code may be sent. */
  nextSendAt?: string;

  constructor(status: number, code: OnboardingApiError["code"], message?: string, missing?: string[]) {
    super(message || code);
    this.name = "OnboardingApiError";
    this.status = status;
    this.code = code;
    this.missing = missing;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

const KNOWN_CODES = new Set<string>([
  "invalid_token",
  "token_expired",
  "locked",
  "invalid_slot",
  "invalid_file_type",
  "file_too_large",
  "too_many_files",
  "missing_required",
  "rate_limited",
  "invalid_request",
  "missing_file",
  "not_found",
  "invalid_code",
  "code_expired",
  "too_many_attempts",
  "sms_failed",
  "phone_not_verified",
  "not_ready",
  "invalid_handle",
  "expired",
  "already_used",
  "unavailable",
]);

/** Code for a status when the body carries none (a proxy's 413, for instance has no JSON). */
const codeForStatus = (status: number): OnboardingApiError["code"] => {
  switch (status) {
    case 400:
      return "invalid_request";
    case 404:
      // only a fallback: a `not_found` on a file route carries its code in the body
      return "invalid_token";
    case 410:
      return "token_expired";
    case 409:
      return "locked";
    case 413:
      return "file_too_large";
    case 415:
      return "invalid_file_type";
    case 422:
      return "missing_required";
    case 429:
      return "rate_limited";
    default:
      return "unknown";
  }
};

const toPublicError = (status: number, bodyText: string, retryAfter?: string | null): OnboardingApiError => {
  let code: OnboardingApiError["code"] = codeForStatus(status);
  let message: string | undefined;
  let missing: string[] | undefined;
  let reason: string | undefined;
  let field: string | undefined;
  let attemptsLeft: number | undefined;
  let nextSendAt: string | undefined;
  try {
    const body = JSON.parse(bodyText);
    if (typeof body?.error === "string" && KNOWN_CODES.has(body.error)) code = body.error as PublicErrorCode;
    if (typeof body?.message === "string") message = body.message;
    if (typeof body?.reason === "string") reason = body.reason;
    if (typeof body?.field === "string") field = body.field;
    if (typeof body?.attemptsLeft === "number") attemptsLeft = body.attemptsLeft;
    if (typeof body?.nextSendAt === "string") nextSendAt = body.nextSendAt;
    if (Array.isArray(body?.missing)) missing = body.missing.filter((entry: unknown) => typeof entry === "string");
  } catch {
    // no JSON body
  }
  const error = new OnboardingApiError(status, code, message, missing);
  error.reason = reason;
  error.field = field;
  error.attemptsLeft = attemptsLeft;
  error.nextSendAt = nextSendAt;
  const seconds = retryAfter ? Number(retryAfter) : NaN;
  if (Number.isFinite(seconds) && seconds > 0) error.retryAfterSeconds = Math.ceil(seconds);
  return error;
};

const publicUrl = (token: string, path = "") =>
  `${configuration.serverUrl}/public/onboarding/${encodeURIComponent(token)}${path}`;

async function publicCall<T>(token: string, path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(publicUrl(token, path), {
      ...init,
      headers: { Accept: "application/json", ...(init.body ? { "Content-Type": "application/json" } : {}) },
    });
  } catch {
    throw new OnboardingApiError(0, "network");
  }
  if (response.status === 204) return undefined as T;
  if (!response.ok) throw toPublicError(response.status, await response.text().catch(() => ""), response.headers?.get?.("Retry-After"));
  return (await response.json()) as T;
}

export const onboardingPublicAPI = {
  getLead: (token: string): Promise<PublicLeadResponse> => publicCall(token, ""),

  /** Subsets are allowed; the server keeps a positive list of fields. */
  saveProfile: (
    token: string,
    body: { profile?: Partial<LeadProfile>; answers?: Partial<LeadAnswers> },
  ): Promise<PublicLeadResponse> => publicCall(token, "/profile", { method: "PUT", body: JSON.stringify(body) }),

  /** What the server holds of one stored file: `404 not_found`, `409 locked`, `410 token_expired`, `429 rate_limited`. */
  previewFile: (token: string, fileId: string): Promise<FilePreview> => publicCall(token, `/files/${encodeURIComponent(fileId)}/preview`),

  /** The bytes of a stored image or PDF (`.../content`, not cached); `415` for anything else. */
  fileContent: async (token: string, fileId: string): Promise<Blob> => {
    let response: Response;
    try {
      response = await fetch(publicUrl(token, `/files/${encodeURIComponent(fileId)}/content`));
    } catch {
      throw new OnboardingApiError(0, "network");
    }
    if (!response.ok) throw toPublicError(response.status, await response.text().catch(() => ""), response.headers?.get?.("Retry-After"));
    return response.blob();
  },

  /**
   * The one-time sign-in after the submit: `{ handle }` (from the answer to the submit) for `{ url }`, a relative
   * `/auth/magic?token=...` that is good for 5 minutes. `409 not_ready` (the access is not made yet), `403 invalid_handle`,
   * `410 expired`, `409 already_used`, `409 unavailable` (e.g. sign-in by SSO only), `429 rate_limited`.
   */
  portalLogin: (token: string, handle: string): Promise<{ url: string }> =>
    publicCall(token, "/portal-login", { method: "POST", body: JSON.stringify({ handle }) }),

  /**
   * An address suggestion for a company name, from public sources on the web. `409 locked`, `429 rate_limited`,
   * `400 invalid_request`. The page only asks after a pause, once per name, and only when `view.lookup.enabled`.
   */
  companyLookup: (token: string, companyName: string): Promise<CompanyLookupResult> =>
    publicCall(token, "/company-lookup", { method: "POST", body: JSON.stringify({ companyName }) }),

  deleteFile: (token: string, fileId: string): Promise<void> =>
    publicCall(token, `/files/${encodeURIComponent(fileId)}`, { method: "DELETE" }),

  submit: (token: string): Promise<PublicLeadResponse> => publicCall(token, "/submit", { method: "POST" }),

  /**
   * Sends the SMS code. Without `phone` to the stored number; with one the number is changed (the server stores it
   * and the verification lapses). `429` carries `nextSendAt` and `Retry-After`; `400 invalid_request` has
   * `field: phone`; `502 sms_failed` and the others a German `message`.
   */
  sendPhoneCode: (token: string, phone?: string): Promise<{ phone?: string | PublicPhoneState; nextSendAt?: string }> =>
    publicCall(token, "/phone/send", { method: "POST", ...(phone === undefined ? {} : { body: JSON.stringify({ phone }) }) }),

  /** `400 invalid_code` (with `attemptsLeft`), `400 code_expired`, `429 too_many_attempts`, `409 locked`. */
  verifyPhoneCode: (token: string, code: string): Promise<{ verified: true }> =>
    publicCall(token, "/phone/verify", { method: "POST", body: JSON.stringify({ code }) }),

  /**
   * The page was opened (once per tab session). Fire and forget: no body, nothing sent but the token in
   * the path, no cookie, and any failure - a 404 from an older server above all - is swallowed.
   */
  recordOpen: async (token: string): Promise<void> => {
    try {
      await fetch(publicUrl(token, "/open"), { method: "POST" });
    } catch {
      // never disturbs the page
    }
  },

  /** A step was entered. Same rules as {@link recordOpen}; `step` is one of the server's slugs. */
  recordProgress: async (token: string, step: string): Promise<void> => {
    try {
      await fetch(publicUrl(token, "/progress"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ step }),
      });
    } catch {
      // never disturbs the page
    }
  },

  /**
   * The logo the person uploaded, for the preview after a reload (`GET .../logo`, token in the path,
   * no cookie). `null` when the server has no such route (yet) or no logo: the page then shows the
   * file name only.
   */
  getLogo: async (token: string): Promise<Blob | null> => {
    try {
      const response = await fetch(publicUrl(token, "/logo"), { headers: { Accept: "image/*" } });
      if (!response.ok) return null;
      const blob = await response.blob();
      return blob.type.startsWith("image/") ? blob : null;
    } catch {
      return null;
    }
  },

  /**
   * Upload one file. `XMLHttpRequest` because `fetch` reports no upload progress; the returned
   * `abort` cancels a running upload.
   */
  uploadFile: (
    token: string,
    slot: DataSlot,
    file: File,
    onProgress?: (fraction: number) => void,
  ): { promise: Promise<PublicFileView>; abort: () => void } => {
    const request = new XMLHttpRequest();
    const promise = new Promise<PublicFileView>((resolve, reject) => {
      request.open("POST", publicUrl(token, `/files?slot=${encodeURIComponent(slot)}`));
      request.setRequestHeader("Accept", "application/json");
      request.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
      };
      // A server that refuses a big upload early may cut the connection after it has answered: the
      // status, when the browser kept one, is the better account than "network".
      request.onerror = () =>
        reject(
          request.status >= 400
            ? toPublicError(request.status, request.responseText || "", request.getResponseHeader?.("Retry-After"))
            : new OnboardingApiError(0, "network"),
        );
      request.onabort = () => reject(new OnboardingApiError(0, "network", "aborted"));
      request.onload = () => {
        if (request.status >= 200 && request.status < 300) {
          try {
            resolve(JSON.parse(request.responseText) as PublicFileView);
          } catch {
            reject(new OnboardingApiError(request.status, "unknown"));
          }
        } else {
          reject(toPublicError(request.status, request.responseText || "", request.getResponseHeader?.("Retry-After")));
        }
      };
      const form = new FormData();
      form.append("file", file);
      request.send(form);
    });
    return { promise, abort: () => request.abort() };
  },
};

// ---------------------------------------------------------------------------
// Admin half
// ---------------------------------------------------------------------------

const ADMIN_BASE = () => `${configuration.serverUrl}/administration/onboarding-leads`;

/** Error codes of the admin routes (contract: "Fehler der Admin-Routen") as readable German text. */
export const ADMIN_ERROR_TEXT: Record<string, string> = {
  invalid_request: "Die Eingabe ist ungültig.",
  not_found: "Dieser Lead oder diese Datei existiert nicht (mehr).",
  invalid_state: "Das ist im aktuellen Status des Leads nicht möglich.",
  analysis_running: "Die Analyse läuft noch. Bitte warte, bis sie fertig ist.",
};
export const ADMIN_FORBIDDEN_TEXT = "Das Leads Cockpit ist nur für Plattform-Administratoren zugänglich.";

/** A refused admin call; `code` is the contract's error code when the server sent one. */
export class OnboardingAdminError extends ApplicationError {
  code?: string;
  /** `invalid_request`: the technical name of the field and the server's own sentence about it (for administrators, in the du form). */
  field?: string;
  detail?: string;

  constructor(message: string, status: number, code?: string) {
    super(message, status);
    this.name = "OnboardingAdminError";
    this.code = code;
  }
}

const processAdminResponse = async (response: Response) => {
  if (response.ok) return response.status === 204 ? undefined : response.json();
  if (response.status === 401) throw new UnauthorizedError();
  if (response.status === 403) throw new OnboardingAdminError(ADMIN_FORBIDDEN_TEXT, 403, "forbidden");
  let message = `${response.status} ${response.statusText}`;
  let code: string | undefined;
  let field: string | undefined;
  let detail: string | undefined;
  try {
    const body = await response.json();
    if (typeof body?.error === "string") code = body.error;
    if (typeof body?.field === "string") field = body.field;
    if (typeof body?.message === "string") detail = body.message;
    message = (code && ADMIN_ERROR_TEXT[code]) || body.message || body.error || message;
  } catch {
    // no JSON body
  }
  const error = new OnboardingAdminError(message, response.status, code);
  error.field = field;
  error.detail = detail;
  throw error;
};

const adminCall = async (path: string, init: RequestInit = {}) =>
  processAdminResponse(await authenticatedFetch(`${ADMIN_BASE()}${path}`, init));

/** The link the invited person opens; assembled here, never by the server. */
export const onboardingLinkFor = (token: string) => `${window.location.origin}/onboarding/${token}`;

export const onboardingAdminAPI = {
  list: (): Promise<AdminLeadSummary[]> => adminCall(""),
  get: (id: string): Promise<AdminLeadDetail> => adminCall(`/${encodeURIComponent(id)}`),
  create: (input: CreateLeadInput): Promise<{ lead: AdminLeadSummary; token: string; warnings?: InviteWarning[] }> =>
    adminCall("", { method: "POST", body: JSON.stringify(input) }),
  /** Whether the address already has an account or open invitations (`400 invalid_request`, `field: email`). A hint, never a block. */
  checkContact: (email: string): Promise<{ existingUser: boolean; openLeads: number }> =>
    adminCall("/check-contact", { method: "POST", body: JSON.stringify({ email }) }),
  /** `expiresInDays` (1-365) is optional; the server defaults to 30. */
  regenerateLink: (id: string, expiresInDays?: number): Promise<{ token: string }> =>
    adminCall(`/${encodeURIComponent(id)}/regenerate-link`, {
      method: "POST",
      ...(expiresInDays !== undefined ? { body: JSON.stringify({ expiresInDays }) } : {}),
    }),
  analyze: (id: string): Promise<AdminLeadDetail> => adminCall(`/${encodeURIComponent(id)}/analyze`, { method: "POST" }),
  /**
   * Sets a NEW one-time password for the contact and sends the access mail again (the old password, a chosen one
   * included, stops working once the mail is out). Success is `credentialsResent: true` in the answer and nothing
   * else: a time stamp from an earlier mail proves nothing. `409 invalid_state` (no tenant, SSO, the person has
   * changed the password, a mail went out minutes ago) and `502 mail_failed` (the password stays as it was) carry a
   * German `message`.
   */
  resendCredentials: (id: string): Promise<AdminLeadDetail & { credentialsResent?: boolean }> =>
    adminCall(`/${encodeURIComponent(id)}/resend-credentials`, { method: "POST" }),
  /** Moves the uploads into the tenant (again). `409 invalid_state` with a German `message` when it is not the time. */
  adoptFiles: (id: string): Promise<AdminLeadDetail> => adminCall(`/${encodeURIComponent(id)}/adopt-files`, { method: "POST" }),
  /** Makes site, buildings and assets from the adopted files (again). `409 invalid_state` with a German `message`. */
  startImport: (id: string): Promise<AdminLeadDetail> => adminCall(`/${encodeURIComponent(id)}/import`, { method: "POST" }),
  /** Deletes the lead and its files that are not in the tenant yet, for good (never the tenant's data). No body; idempotent (204 for a lead that is gone). */
  remove: (id: string): Promise<void> => adminCall(`/${encodeURIComponent(id)}`, { method: "DELETE" }),
  /** The "new lead" mail to the team, again (it failed at the submit). 404 on a server that does not have the route yet. */
  resendTeamMail: (id: string): Promise<AdminLeadDetail> =>
    adminCall(`/${encodeURIComponent(id)}/resend-team-mail`, { method: "POST" }),
  /**
   * Creates what is missing after the submit: the tenant, the access mail, the adoption of the files (`200` detail,
   * `409 invalid_state` with a German sentence). Nothing is made twice.
   */
  provision: (id: string): Promise<AdminLeadDetail> =>
    adminCall(`/${encodeURIComponent(id)}/provision`, { method: "POST" }),


  /** A stored file as a Blob; the download needs the auth header, so it cannot be a plain link. */
  downloadFile: async (id: string, fileId: string): Promise<Blob> => {
    const response = await authenticatedFetch(
      `${ADMIN_BASE()}/${encodeURIComponent(id)}/files/${encodeURIComponent(fileId)}`,
      { method: "GET" },
    );
    if (!response.ok) {
      if (response.status === 401) throw new UnauthorizedError();
      if (response.status === 403) throw new OnboardingAdminError(ADMIN_FORBIDDEN_TEXT, 403, "forbidden");
      throw new OnboardingAdminError(
        response.status === 404 ? ADMIN_ERROR_TEXT.not_found : `${response.status} ${response.statusText}`,
        response.status,
        response.status === 404 ? "not_found" : undefined,
      );
    }
    return response.blob();
  },
};
